/**
 * Functions for managing video players and controls
 */

import Player from '@vimeo/player';

/**
 * Set up play/pause button handlers for videos
 * @param {HTMLElement} context - Context element for event delegation
 */
export function setupPlayPauseHandlers(context) {
  // Use the context for event delegation to scope the listener
  context.addEventListener('click', (e) => {
    // Handle play/pause button clicks
    const el = e.target.closest('#playPauseButton');
    if (el) {
      e.stopPropagation(); // Prevent event from bubbling up to avoid slide changes
      togglePlayPause(el, context);
    }
  });
}

/**
 * Toggle play/pause state of a video
 * @param {HTMLElement} button - Play/pause button element
 * @param {HTMLElement} context - Context element
 */
export function togglePlayPause(button, context = document) {
  // Find the associated video element
  const videoElement = button.closest('.swiper-slide').querySelector('video');
  if (!videoElement) return;

  // Get all pause and play icons
  const pauseIcon = button.querySelector('.pause');
  const playIcon = button.querySelector('.play');
  const srText = button.querySelector('.visually-hidden');

  if (videoElement.paused) {
    // Pause any other playing videos first except those in PromoTile
    context.querySelectorAll('video').forEach((v) => {
      if (v !== videoElement && !v.paused && !v.closest('.PromoTile')) {
        v.pause();
        // Find and update the associated button
        const otherButton = v.closest('.swiper-slide').querySelector('#playPauseButton');
        if (otherButton) {
          otherButton.querySelector('.pause').style.display = 'none';
          otherButton.querySelector('.play').style.display = 'block';
          otherButton.querySelector('.visually-hidden').textContent = 'Play';
          otherButton.setAttribute('aria-pressed', 'false');
        }
      }
    });

    // Play this video
    videoElement.play().catch((err) => {
      console.warn('ud83cudfac Could not play video:', err);
    });

    // Update button state
    pauseIcon.style.display = 'block';
    playIcon.style.display = 'none';
    srText.textContent = 'Pause';
    button.setAttribute('aria-pressed', 'true');
    button.setAttribute('aria-label', 'Pause video');
  } else {
    // Pause this video
    videoElement.pause();

    // Update button state
    pauseIcon.style.display = 'none';
    playIcon.style.display = 'block';
    srText.textContent = 'Play';
    button.setAttribute('aria-pressed', 'false');
    button.setAttribute('aria-label', 'Play video');
  }
}

/**
 * Initialize Vimeo players for external videos
 * @param {HTMLElement} context - Context element
 * @returns {Array} - Array of initialized Vimeo player instances
 */
export function initializeVimeoPlayers(context = document) {
  const vimeoContainers = context.querySelectorAll('.product-media--vimeo');
  const players = [];

  vimeoContainers.forEach((container) => {
    // Get the Vimeo video ID from the container
    const videoId = container.getAttribute('data-vimeo-id');
    if (!videoId) return;

    // Create a new Vimeo player
    const player = new Player(container, {
      id: videoId,
      width: '100%',
      loop: true,
      title: false,
      byline: false,
      portrait: false,
      controls: false,
      muted: true,
    });

    // Add event handlers
    player.on('play', () => {
      // Update associated button if exists
      const slide = container.closest('.swiper-slide');
      if (slide) {
        const button = slide.querySelector('#playPauseButton');
        if (button) {
          button.querySelector('.pause').style.display = 'block';
          button.querySelector('.play').style.display = 'none';
          button.setAttribute('aria-pressed', 'true');
        }
      }
    });

    player.on('pause', () => {
      // Update associated button if exists
      const slide = container.closest('.swiper-slide');
      if (slide) {
        const button = slide.querySelector('#playPauseButton');
        if (button) {
          button.querySelector('.pause').style.display = 'none';
          button.querySelector('.play').style.display = 'block';
          button.setAttribute('aria-pressed', 'false');
        }
      }
    });

    players.push(player);
  });

  return players;
}

/**
 * Play or pause a Vimeo video
 * @param {Object} player - Vimeo player instance
 * @param {boolean} shouldPlay - Whether to play or pause
 */
export function controlVimeoPlayer(player, shouldPlay) {
  if (!player) return;

  if (shouldPlay) {
    player.play().catch((err) => {
      console.warn('ud83cudfac Could not play Vimeo video:', err);
    });
  } else {
    player.pause().catch((err) => {
      console.warn('ud83cudfac Could not pause Vimeo video:', err);
    });
  }
}
